/**
 * hosted-handoff-client.ts — the CLI's transport for v4-S10 E2EE session handoff.
 *
 * Mirrors `HostedCodeClient`: bearer = the SAME room token (same resolution via
 * `resolveProject`), and the server only ever sees ciphertext + the envelope
 * residual. The three handoff calls go to the sibling routes:
 *
 *   POST /api/sync/handoff        — store one envelope
 *   GET  /api/sync/handoff        — list envelopes (no ciphertext), ?limit&before
 *   GET  /api/sync/handoff/[id]   — fetch one full ciphertext row
 *
 * The public-key directory is NOT re-exposed on the handoff routes: this client
 * composes a `HostedCodeClient` and reuses the EXISTING `publish_key` /
 * `member_keys` actions on POST /api/sync/code (member keys are room-scoped,
 * not artifact-kind-scoped). That also gives us `whoami` for free: the member
 * uuid comes back from `publish_key` (the only cross-machine-consistent id).
 */

import { homedir } from "node:os";
import { loadVerifyStore } from "./verify-store.js";
import { HostedCodeClient } from "./hosted-code-client.js";
import {
  shareHandoff,
  fetchHandoffArtifact,
  type HandoffEnvelope,
  type HandoffListEntry,
  type HandoffPayload,
  type HandoffRow,
  type HandoffTransport,
  type FetchHandoffResult,
} from "./handoff.js";

export class HostedHandoffClient implements HandoffTransport {
  private readonly codeClient: HostedCodeClient;

  constructor(
    private readonly baseUrl: string,
    private readonly token: string,
    private readonly roomId: string,
    private readonly home: string = homedir(),
    private readonly fetchImpl: typeof fetch = fetch,
  ) {
    this.codeClient = new HostedCodeClient(baseUrl, token, roomId, home, fetchImpl);
  }

  private async http(path: string, init?: RequestInit): Promise<Record<string, unknown>> {
    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
        ...(init?.headers ?? {}),
      },
    });
    if (!res.ok) {
      throw new Error(`aethereum handoff request failed (${res.status})`);
    }
    const json = (await res.json()) as Record<string, unknown>;
    if (json.ok === false && typeof json.notice === "string") {
      // Daily-cap sentinel: informational, surfaced to the caller, never a crash.
      throw new Error(json.notice);
    }
    return json;
  }

  // ── HandoffTransport ───────────────────────────────────────────────────────

  async storeHandoff(input: HandoffEnvelope): Promise<{ id: string }> {
    const r = await this.http("/api/sync/handoff", { method: "POST", body: JSON.stringify(input) });
    return { id: String(r.id) };
  }

  async listHandoffs(opts?: { limit?: number; before?: string }): Promise<HandoffListEntry[]> {
    const params = new URLSearchParams();
    if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
    if (opts?.before) params.set("before", opts.before);
    const query = params.toString();
    const r = await this.http(`/api/sync/handoff${query ? `?${query}` : ""}`, { method: "GET" });
    return (r.artifacts as HandoffListEntry[] | undefined) ?? [];
  }

  async fetchHandoff(id: string): Promise<HandoffRow | null> {
    const res = await this.fetchImpl(`${this.baseUrl}/api/sync/handoff/${encodeURIComponent(id)}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${this.token}` },
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`aethereum handoff fetch failed (${res.status})`);
    const json = (await res.json()) as Record<string, unknown>;
    return (json.row as HandoffRow | null) ?? null;
  }

  // Key directory: the existing publish_key / member_keys actions on /api/sync/code.
  async publishPublicKey(input: {
    identityPub: string;
    wrapPub: string;
    wrapPubSig: string;
    alg: string;
  }): Promise<void> {
    await this.codeClient.publishPublicKey(input);
  }

  async memberPublicKeys(): Promise<Awaited<ReturnType<HostedCodeClient["memberPublicKeys"]>>> {
    return this.codeClient.memberPublicKeys();
  }

  // ── high-level, keystore-bearing surface (what the commands call) ──────────

  /** Resolve THIS member's directory uuid (provisions + publishes on first use). */
  async whoami(): Promise<string> {
    return this.codeClient.provision();
  }

  /** Encrypt + store a session handoff. The plaintext never leaves this process. */
  async shareSession(
    payload: HandoffPayload,
    opts?: {
      targetMember?: string;
      /** v6-S1: informed-consent hook for first-contact recipients (CLI TTY path). */
      onFirstContact?: (
        recipients: Array<{ member: string; identityPub: string }>,
      ) => Promise<boolean> | boolean;
    },
  ): Promise<{
    id: string;
    recipients: number;
    dropped: number;
    excluded: string[];
    firstContact: string[];
    policyDegraded: boolean;
  }> {
    const me = await this.whoami();
    const store = loadVerifyStore(this.roomId, this.home);
    const policy = await this.codeClient.shareSettings();
    const res = await shareHandoff(this, {
      roomId: this.roomId,
      member: me,
      payload,
      targetMember: opts?.targetMember,
      pins: store.pins,
      verified: store.verified,
      requireVerified: policy.requireVerified,
      onFirstContact: opts?.onFirstContact,
      home: this.home,
    });
    return { ...res, policyDegraded: policy.degraded };
  }

  /** Fetch + verify + decrypt one handoff for THIS member. */
  async fetchSession(id: string): Promise<FetchHandoffResult | null> {
    const me = await this.whoami();
    const pins = loadVerifyStore(this.roomId, this.home).pins;
    return fetchHandoffArtifact(this, { roomId: this.roomId, member: me, id, pins, home: this.home });
  }
}
